// Vuex
import type { GetterTree, MutationTree } from 'vuex'
import AuthService from '@/services/AuthService'
import {
  ContactLoginDTO,
  ContactRegisterDTO,
  ContactSearchDTO,
  ContactNewPasswordDTO,
  ContactResetPasswordDTO,
  UserInfo,
  ContactLoginResponseDTO,
  ContactNewPasswordResponseDTO,
  ContactResetPasswordResponseDTO,
  ContactRegisterResponseDTO,
  UpdateUserInfoDTO,
} from '@/services/AuthService.interface'

// Composables
import { useDomainRedirect } from '@/composables/useDomainRedirect'

// Services
import YoonlyService from '@/services/YoonlyService'

// Extended Vuex types and store
import { State, TypedActionTree, LoadingErrorState } from '../types'

// Utils
import { extractErrorMessage } from '@/utils/index'

// State — keep in sync with auth.user after session refresh (login/register already set it)
import loggedInUser from '@/state/loggedInUser'

// Mappers
import AuthDataMapper from '@/mappers/AuthDataMapper'

// Config
import { CookieName } from '@/config/cookies'
import {LoginOrigin} from "@/config/LoginOrigin"
import CSEService from '@/services/CSEService'

import Cookies from 'js-cookie'
import router from '@/router'

// Define the type for all actions
export type Actions =
  | 'login'
  | 'logout'
  | 'refresh'
  | 'register'
  | 'search'
  | 'resetPassword'
  | 'newPassword'
  | 'updateUserInfo'
  | 'changePassword'
  | 'validateTokens'
  | 'handleBookingUILogin'
  | 'handleCustomerUILogin'

export interface AuthState extends LoadingErrorState<Actions> {
  user: UserInfo | null
  calledRefresh: boolean
}

// Initial State
const state: AuthState = {
  user: null,
  loading: {},
  error: {},
  calledRefresh: false,
}

// Mutations
const mutations: MutationTree<AuthState> = {
  setUser(state, payload: UserInfo) {
    state.user = payload
  },
  setLoading(state, { action, status }: { action: Actions; status: boolean }) {
    state.loading[action] = status
  },
  setError(
    state,
    { action, message }: { action: Actions; message: string | null }
  ) {
    state.error[action] = message
  },
  setCalledRefresh(state, value: boolean) {
    state.calledRefresh = value
  },
}

// Actions
const actions: TypedActionTree<Actions, AuthState, State> = {
  async login(
    { commit },
    payload: ContactLoginDTO
  ): Promise<ContactLoginResponseDTO> {
    const action = 'login'
    commit('setLoading', { action, status: true })
    commit('setError', { action, message: null })
    try {
      const authService = new AuthService()
      const response = await authService.login(payload)
      if (!response) throw new Error('response cannot be undefined in login')
      return response
    } catch (error: any) {
      const message = extractErrorMessage(error, 'Login failed')
      commit('setError', {
        action,
        message,
      })
      throw error
    } finally {
      commit('setLoading', { action, status: false })
    }
  },

  async handleBookingUILogin({ dispatch }): Promise<boolean> {
    const hashParams = new URLSearchParams(window.location.hash.substring(1))
    const hashQueryParams = Object.fromEntries(hashParams.entries())

    if (hashQueryParams['origin'] && hashQueryParams['origin'] === LoginOrigin.BOOKING_UI) {
      try {
        const userInfo = await dispatch('refresh')

        if (!userInfo) {
          throw new Error('User not authenticated')
        }

        
        return true // Continue app initialization
      } catch (err) {
        console.error(
          'Booking UI login detected, but authentication failed:',
          err
        )
        router.push('/account/login')
        return false
      }
    }

    return false // No Booking UI login detected, continue as usual
  },

  async handleCustomerUILogin({ dispatch }): Promise<boolean> {
    const hashParams = new URLSearchParams(window.location.hash.substring(1))
    const hashQueryParams = Object.fromEntries(hashParams.entries())

    if (hashQueryParams['origin'] && hashQueryParams['origin'] === LoginOrigin.CUSTOMER_UI) {
      try {
        const userInfo = await dispatch('refresh')

        if (!userInfo) {
          throw new Error('User not authenticated')
        }

        return true // Continue app initialization
      } catch (err) {
        console.error(
          'Customer UI login detected, but authentication failed:',
          err
        )
        router.push('/account/login')
        return false
      }
    }

    return false // No Customer UI login detected, continue as usual
  },

  async logout({ commit }) {
    const action = 'logout'
    commit('setLoading', { action, status: true })
    commit('setError', { action, message: null })

    const cseService = new CSEService()

    try {
      // Step 1: Retrieve company from cookies
      const company = Cookies.get(CookieName.COMPANY)

      // Step 2: Determine redirect URL
      const { isCSE } = useDomainRedirect()
      const isYoonly = new YoonlyService().isYoonly
      const locale = (window.env?.VUE_APP_LOCALE ?? 'fr-FR') as string
      let redirectUrl = '/' // Default redirect

      if (isYoonly) {
        redirectUrl = locale === 'nl-NL'
          ? 'https://www.yoonly.eu/be-nl/'
          : 'https://www.yoonly.eu/be-fr/'
        console.log('[Yoonly Logout] Redirecting to', redirectUrl)
      } else if (isCSE.value && company) {
        // Generate login URL with credentials only
        const generatedUrl = await cseService.getRedirectUrlWithCredentials(
          `/page/${company}/${company}`
        )
        redirectUrl = generatedUrl || `/page/${company}/login.html` // Fallback to the plain URL
      } else if (isCSE.value) {
        console.error(
          'Company cookie is missing. Falling back to default home.'
        )
      }

      // Step 3: Perform logout after redirect URL is determined
      const authService = new AuthService()
      authService.logout()

      // Step 4: Redirect after logout
      window.location.href = redirectUrl
    } catch (error) {
      const message = extractErrorMessage(error, 'Logout failed')
      commit('setError', { action, message })
    } finally {
      commit('setLoading', { action, status: false })
      // Ensure refresh on next login
      commit('setCalledRefresh', false)
    }
  },

  async refresh({ commit }): Promise<UserInfo | null> {
    const action = 'refresh'
    commit('setLoading', { action, status: true })
    commit('setError', { action, message: null })
    commit('setCalledRefresh', true)

    try {
      const authService = new AuthService()
      const userInfo: UserInfo | null = await authService.refresh()
      if (!userInfo) throw new Error('Refresh failed')
      commit('setUser', userInfo)
      loggedInUser.value = userInfo
      return userInfo
    } catch (error) {
      const message = extractErrorMessage(error, 'refresh failed')

      commit('setError', {
        action,
        message,
      })

      throw error
    } finally {
      commit('setLoading', { action, status: false })
    }
  },

  async register(
    { commit },
    payload: ContactRegisterDTO
  ): Promise<ContactRegisterResponseDTO | null> {
    const action = 'register'
    commit('setLoading', { action, status: true })
    commit('setError', { action, message: null })
    try {
      const authService = new AuthService()
      return await authService.register(payload)
    } catch (error) {
      const message = extractErrorMessage(error, 'Registration failed')

      commit('setError', {
        action,
        message,
      })

      throw error
    } finally {
      commit('setLoading', { action, status: false })
    }
  },

  async search({ commit }, payload: ContactSearchDTO) {
    const action = 'search'
    commit('setLoading', { action, status: true })
    commit('setError', { action, message: null })
    try {
      const authService = new AuthService()
      const response = await authService.search(payload)
      return response
    } catch (error: any) {
      const message = extractErrorMessage(error, 'Search failed')

      commit('setError', {
        action,
        message,
      })

      throw error
    } finally {
      commit('setLoading', { action, status: false })
    }
  },

  async resetPassword(
    { commit },
    payload: ContactResetPasswordDTO
  ): Promise<ContactResetPasswordResponseDTO | null> {
    const action = 'resetPassword'
    commit('setLoading', { action, status: true })
    commit('setError', { action, message: null })
    try {
      const authService = new AuthService()
      return await authService.resetPassword(payload)
    } catch (error: any) {
      const message = extractErrorMessage(error, 'Password reset failed')

      commit('setError', {
        action,
        message,
      })

      throw error
    } finally {
      commit('setLoading', { action, status: false })
    }
  },

  async newPassword(
    { commit },
    payload: ContactNewPasswordDTO
  ): Promise<ContactNewPasswordResponseDTO | null> {
    const action = 'newPassword'
    commit('setLoading', { action, status: true })
    commit('setError', { action, message: null })
    try {
      const authService = new AuthService()
      return await authService.newPassword(payload)
    } catch (error) {
      const message = extractErrorMessage(error, 'Password update failed')

      commit('setError', {
        action,
        message,
      })

      throw error
    } finally {
      commit('setLoading', { action, status: false })
    }
  },

  async updateUserInfo(
    { commit, state },
    updates: UserInfo
  ): Promise<Partial<UserInfo>> {
    const action = 'updateUserInfo'
    commit('setLoading', { action, status: true })
    commit('setError', { action, message: null })
    try {
      const authService = new AuthService()

      const updatedUser: UserInfo = {
        ...state.user,
        ...updates,
      }
      const dto: UpdateUserInfoDTO =
        AuthDataMapper.buildUpdateUserInfoDTO(updatedUser)

      await authService.updateUserInfo(dto)
      commit('setUser', updatedUser)
      return updatedUser
    } catch (error) {
      const message = extractErrorMessage(error, 'User info update failed')

      commit('setError', {
        action,
        message,
      })

      throw error
    } finally {
      commit('setLoading', { action, status: false })
    }
  },
  /**
   * Change Password Action
   */
  async changePassword(
    { commit },
    {
      payload,
    }: {
      payload: { oldPassword: string; password: string }
    }
  ): Promise<void> {
    const action = 'changePassword'
    commit('setLoading', { action, status: true })
    commit('setError', { action, message: null })
    try {
      const authService = new AuthService()
      await authService.changePassword(payload)
    } catch (error) {
      const message = extractErrorMessage(error, 'Password change failed')

      commit('setError', {
        action,
        message,
      })

      throw error // Propagate error to the caller
    } finally {
      commit('setLoading', { action, status: false })
    }
  },
  /**
   * Validate tokens
   */

  validateTokens({ commit }, _): void {
    const action = 'changePassword'
    commit('setLoading', { action, status: true })
    commit('setError', { action, message: null })
    const authService = new AuthService()
    try {
      authService.validateTokens()
    } catch (error) {
      const message = extractErrorMessage(error, 'validateTokens failed')

      commit('setError', {
        action,
        message,
      })

      throw error
    } finally {
      commit('setLoading', { action, status: false })
    }
  },
}

// Getters
const getters: GetterTree<AuthState, State> = {
  /**
   * Check if any action is loading
   */
  isLoadingAny: (state: AuthState) => {
    return Object.values(state.loading).some(status => status)
  },

  /**
   * Check if a specific action is loading
   */
  isLoading: (state: AuthState) => (action: Actions) => {
    return !!state.loading[action]
  },

  /**
   * Check if there are any errors
   */
  hasErrorAny: (state: AuthState) => {
    return Object.values(state.error).some(message => message !== null)
  },

  /**
   * Get the error message for a specific action
   */
  getError: (state: AuthState) => (action: Actions) => {
    return state.error[action] || null
  },
}

// Export the Vuex module
export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}
